import { Box, CircularProgress, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { CHART_REFERENCE } from "../../../theme/tokens";
import type { ManagementCenterHPPDResponse, ManagementCenterPPDResponse } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { type ClampRange, fetchedDataToChartDataGroup, getCensusYDomain } from "../utils";
import type { Metric } from "./types";

type Props = {
	data?: ManagementCenterPPDResponse | ManagementCenterHPPDResponse;
	metric: Metric;
	isLoading: boolean;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
};

export const ScheduleVsActualChart: FC<Props> = ({ data, metric, isLoading, trailing, containerSx, clampRange }) => {
	const { timezone } = useDepartmentFlags();

	if (isLoading || !data) {
		return (
			<CommonDashboardBox sx={{ flex: 1, minWidth: 0, minHeight: 250 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: 250,
					}}
				>
					{isLoading ? <CircularProgress size={24} /> : ""}
				</Box>
			</CommonDashboardBox>
		);
	}

	const isPpd = metric === "ppd";
	const scheduledData = isPpd ? (data as ManagementCenterPPDResponse).ppdScheduled : (data as ManagementCenterHPPDResponse).hppdScheduled;
	const actualData = isPpd ? (data as ManagementCenterPPDResponse).ppdActual : (data as ManagementCenterHPPDResponse).hppdActual;
	const censusData = data.census;

	const targetData = isPpd ? (data as ManagementCenterPPDResponse).targetPpd : (data as ManagementCenterHPPDResponse).targetHppd;
	const hasTarget = targetData && Object.keys(targetData).length > 0;

	const transformedDataLeft = [
		fetchedDataToChartDataGroup({
			data: scheduledData,
			name: isPpd ? "SCHEDULED PPD" : "SCHEDULED HPPD",
			timezone,
			clampRange,
			color: CHART_REFERENCE.SCHEDULED,
		}),
		fetchedDataToChartDataGroup({
			data: actualData,
			name: isPpd ? "ACTUAL PPD" : "ACTUAL HPPD",
			timezone,
			clampRange,
			color: CHART_REFERENCE.ACTUAL,
		}),
		...(hasTarget
			? [
					fetchedDataToChartDataGroup({
						data: targetData,
						name: "TARGET",
						timezone,
						clampRange,
						color: CHART_REFERENCE.TARGET,
						strokeDasharray: CHART_REFERENCE.TARGET_DASH,
					}),
				]
			: []),
	];

	const transformedDataRight = [
		fetchedDataToChartDataGroup({
			data: censusData,
			name: "CENSUS",
			timezone,
			clampRange,
			color: CHART_REFERENCE.CENSUS,
		}),
	];

	const targetValues = hasTarget ? Object.values(targetData) : [];
	const allScheduledValues = Object.values(scheduledData);
	const allActualValues = Object.values(actualData);
	const allValues = [...allScheduledValues, ...allActualValues, ...targetValues];
	const maxValue = allValues.length > 0 ? Math.round(Math.max(...allValues) + (isPpd ? 3 : 0.1)) : 0;
	const minValue = allValues.length > 0 ? Math.max(Math.floor(Math.min(...allValues) - (isPpd ? 3 : 0.1)), 0) : 0;
	const censusDomain = getCensusYDomain(censusData);

	return (
		<CommonDashboardBox sx={{ flex: 1, minWidth: 0, position: "relative", ...containerSx }}>
			{trailing && <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>{trailing}</Box>}
			<Box
				sx={{
					width: "100%",
					height: containerSx ? "100%" : 350,
				}}
			>
				<InHouseLineChart
					margin={{ top: 34, right: 0, bottom: 0, left: 20 }}
					width="100%"
					height="100%"
					leftAxisData={{
						dataGroups: transformedDataLeft,
						maxValue,
						minValue,
						tickRenderer: (value) => (isPpd ? formatCurrency(value) : `${Number.parseFloat(value.toFixed(1))}`),
					}}
					rightAxisData={{
						dataGroups: transformedDataRight,
						maxValue: censusDomain?.max,
						minValue: censusDomain?.min,
						tickRenderer: (value) => `${value}${value === censusDomain?.max ? "\uD83E\uDDD1" : ""}`,
					}}
					tooltipSortOrder={{ [`SCHEDULED ${metric.toUpperCase()}`]: 0, [`ACTUAL ${metric.toUpperCase()}`]: 1, TARGET: 2, CENSUS: 3 }}
				/>
			</Box>
		</CommonDashboardBox>
	);
};
